import {
  CapabilityGrants,
  MOCK_WIDGET_CONTEXT,
  WidgetContext,
  WidgetHandoffRecord,
  WidgetHostHandlers,
} from '@finxone-platform/shared/app-sdk';
import { CustomWidgetInstanceProperties, resolveGrants } from './custom-widget-host.utils';

/**
 * The app builder's canvas preview: the real bundle in the real sandboxed frame,
 * bridged to the *platform* handlers — which on the canvas are backed by the app
 * builder's Mock* services, so a previewed widget sees the same bridge, the same
 * grants and the same error codes it will see in a deployed project.
 *
 * What the canvas cannot serve from that bridge is anything that moves the page
 * the host itself runs in: a widget's nav.goToPage must not navigate the app
 * builder, and a hand-off must not send it off-origin. Those groups are served
 * here instead (createCustomWidgetPreviewHandlers), no-op and in-memory.
 *
 * What the builder *does* get to decide is what the preview looks like: the
 * instance's settings, its grants (so a denied topic reads as denied on the
 * canvas, exactly as it would in production) and the project's theme tokens.
 */

/**
 * Context for a previewed instance: the real widget/instance/settings and the
 * real page being designed, but a fixture identity. `user.id` deliberately stays
 * the mock's, so it matches the `profile` topic fixture a widget reads — a
 * preview must never carry a real customer id. Roles follow the page's role when
 * the canvas knows it, so role-conditional widgets render the right branch.
 */
export function buildCustomWidgetPreviewContext(options: {
  instance: CustomWidgetInstanceProperties;
  instanceId: string;
  page: { zone: string; url: string; role: string };
  theme: Record<string, string>;
  locale: string;
}): WidgetContext {
  return {
    widgetId: options.instance.widgetId,
    instanceId: options.instanceId,
    settings: options.instance.settings ?? {},
    page: options.page,
    theme: options.theme,
    locale: options.locale,
    user: {
      id: MOCK_WIDGET_CONTEXT.user.id,
      roles: options.page.role ? [options.page.role] : MOCK_WIDGET_CONTEXT.user.roles,
    },
  };
}

/** The instance's grants as production would resolve them. */
export function resolvePreviewGrants(instance: CustomWidgetInstanceProperties): CapabilityGrants {
  return resolveGrants(instance);
}

/**
 * The handler groups the canvas keeps to itself, replacing the platform ones.
 *
 * `nav` and `ui` resolve without doing anything: the builder must not be
 * navigated, alerted or blocked on a native confirm by a widget it is only
 * previewing (confirm answers `true`, the same answer the SDK's mock host gives,
 * so the widget's happy path is what gets designed).
 *
 * `handoff` and `state` are in-memory. A hand-off has no vendor to redirect to
 * here, so it completes immediately and lands on the `handoffs` topic — the
 * same round trip the widget reads in production, minus leaving the page.
 */
export function createCustomWidgetPreviewHandlers(): {
  nav: NonNullable<WidgetHostHandlers['nav']>;
  ui: NonNullable<WidgetHostHandlers['ui']>;
  handoff: NonNullable<WidgetHostHandlers['handoff']>;
  state: NonNullable<WidgetHostHandlers['state']>;
  handoffs: {
    get(): Promise<WidgetHandoffRecord[]>;
    subscribe(push: (payload: WidgetHandoffRecord[]) => void): () => void;
  };
} {
  const handoffs: WidgetHandoffRecord[] = [];
  const listeners = new Set<(payload: WidgetHandoffRecord[]) => void>();
  let handoffCounter = 0;
  let widgetState: unknown;
  return {
    nav: {
      goToPage: async () => undefined,
      back: async () => undefined,
    },
    ui: {
      notify: async () => undefined,
      confirm: async () => true,
    },
    handoff: {
      start: async (kind, params) => {
        const handoffId = `preview-handoff-${++handoffCounter}`;
        handoffs.push({
          handoffId,
          kind,
          status: 'completed',
          params,
          result: { status: 'completed' },
          startedAt: new Date().toISOString(),
        });
        listeners.forEach((push) => push(handoffs));
        return { handoffId };
      },
    },
    state: {
      get: async () => widgetState,
      set: async (value) => void (widgetState = value),
    },
    handoffs: {
      get: async () => handoffs,
      subscribe: (push) => {
        push(handoffs);
        listeners.add(push);
        return () => void listeners.delete(push);
      },
    },
  };
}
